import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, Package, Tag,
  Receipt, BarChart3, LogOut, Store, Truck, PieChart, Ticket,
  Users, Building2, Wallet, UserCog, MapPin, RotateCcw,
  Bell, X, AlertTriangle, PackageX, ArrowLeftRight, Gift,
  ClipboardList, DollarSign, Banknote, Image, Megaphone, Shield,
  Maximize2, Minimize2,
} from 'lucide-react';
import { getOrders, getLowStockProducts } from '../services/api';
import { getMyRole } from '../utils/roles';
import toast from 'react-hot-toast';

// ── Notification Sound (Web Audio API — no audio file needed) ──────────────
const playOrderSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Pleasant 3-note chime: C5 → E5 → G5
    [[523.25, 0], [659.25, 0.2], [783.99, 0.4]].forEach(([freq, delay]) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + delay);
      gain.gain.linearRampToValueAtTime(0.45, ctx.currentTime + delay + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.7);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.8);
    });
  } catch (_) {}
};

const ALL_NAV = [
  { section: 'MAIN', adminOnly: false, items: [
    { path: '/',               icon: LayoutDashboard, label: 'Dashboard',       adminOnly: true  },
    { path: '/orders',         icon: ShoppingCart,    label: 'Orders',          adminOnly: false },
    { path: '/live-tracking',  icon: MapPin,          label: 'Live Tracking',   adminOnly: true  },
    { path: '/billing',        icon: Receipt,         label: 'Billing Counter', adminOnly: false },
  ]},
  { section: 'CATALOGUE', adminOnly: true, items: [
    { path: '/products',    icon: Package,         label: 'Products',        adminOnly: true },
    { path: '/categories',  icon: Tag,             label: 'Categories',      adminOnly: true },
    { path: '/inventory',   icon: BarChart3,       label: 'Inventory',       adminOnly: true },
    { path: '/banners',     icon: Image,           label: 'Banners',         adminOnly: true },
    { path: '/offers',      icon: Megaphone,       label: 'Offers & Promos', adminOnly: true },
  ]},
  { section: 'PEOPLE', adminOnly: true, items: [
    { path: '/customers',   icon: Users,    label: 'Customers',        adminOnly: true },
    { path: '/suppliers',   icon: Building2,label: 'Suppliers',        adminOnly: true },
    { path: '/loyalty',     icon: Gift,     label: 'Customer Wallet',  adminOnly: true },
  ]},
  { section: 'OPERATIONS', adminOnly: false, items: [
    { path: '/purchases',   icon: Truck,           label: 'Purchase / GRN',  adminOnly: false },
    { path: '/returns',       icon: RotateCcw,  label: 'Returns & Refunds',      adminOnly: false },
    { path: '/expired-stock',    icon: PackageX,       label: 'Expired Stock',     adminOnly: true },
    { path: '/supplier-returns', icon: ArrowLeftRight, label: 'Supplier Returns',  adminOnly: true },
    { path: '/expenses',    icon: Wallet,          label: 'Expenses',        adminOnly: true },
    { path: '/coupons',     icon: Ticket,          label: 'Coupons',         adminOnly: true },
    { path: '/reports',     icon: PieChart,        label: 'Reports',         adminOnly: true },
  ]},
  { section: 'SETTINGS', adminOnly: true, items: [
    { path: '/staff',        icon: UserCog,      label: 'Staff',           adminOnly: true },
    { path: '/attendance',   icon: ClipboardList,label: 'Attendance',      adminOnly: true },
    { path: '/cash-drawer',  icon: DollarSign,   label: 'Cash Drawer',     adminOnly: true },
    { path: '/payroll',      icon: Banknote,     label: 'Payroll',         adminOnly: true },
    { path: '/sessions',     icon: Shield,       label: 'Active Sessions', adminOnly: true },
  ]},
];

const POLL_INTERVAL = 15000; // 15 seconds

export default function Layout() {
  const navigate  = useNavigate();
  const role      = getMyRole();
  const isCashier = role !== 'admin';

  // ── Notification state ────────────────────────────────────────────────────
  const [newOrderBadge,  setNewOrderBadge]  = useState(0);
  const [notifBanner,    setNotifBanner]    = useState(null);
  const [notifEnabled,   setNotifEnabled]   = useState(false);
  const lastOrderIdRef   = useRef(null);
  const pollTimerRef     = useRef(null);
  const initializedRef   = useRef(false);

  // ── Low stock state ───────────────────────────────────────────────────────
  const [lowStockCount,   setLowStockCount]   = useState(0);
  const [lowStockBanner,  setLowStockBanner]  = useState(null); // newly low items
  const prevLowStockIds = useRef(new Set());

  // Request browser notification permission once
  const requestNotifPermission = useCallback(async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      setNotifEnabled(perm === 'granted');
    } else {
      setNotifEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Show browser notification (works even when tab not focused)
  const showBrowserNotif = useCallback((order) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      new Notification('🛒 New Order Received!', {
        body: `Order ${order.order_number} — ₹${parseFloat(order.total_amount).toFixed(2)}`,
        icon: '/favicon.ico',
        tag: `order-${order.id}`,
        requireInteraction: true,
      });
    } catch (_) {}
  }, []);

  // Poll for new orders
  const pollOrders = useCallback(async () => {
    try {
      const res  = await getOrders({ limit: 5, sort: 'newest' });
      const rows = res.data.data || res.data.orders || [];
      if (!rows.length) return;

      // Filter only online orders (not OFFLINE_BILL)
      const onlineOrders = rows.filter(o =>
        !o.notes || !o.notes.startsWith('OFFLINE_BILL')
      );
      if (!onlineOrders.length) return;

      const latestId = onlineOrders[0].id;

      // First poll — just set the baseline, no alert
      if (!initializedRef.current) {
        lastOrderIdRef.current = latestId;
        localStorage.setItem('lastKnownOrderId', String(latestId));
        initializedRef.current = true;
        return;
      }

      // Restore from localStorage if ref was reset (e.g. page refresh)
      if (lastOrderIdRef.current === null) {
        const stored = parseInt(localStorage.getItem('lastKnownOrderId') || '0');
        lastOrderIdRef.current = stored;
      }

      // Find genuinely new orders
      const newOrders = onlineOrders.filter(o => o.id > lastOrderIdRef.current);
      if (!newOrders.length) return;

      // Update tracking
      lastOrderIdRef.current = latestId;
      localStorage.setItem('lastKnownOrderId', String(latestId));

      // Alert for each new order
      newOrders.forEach((order, idx) => {
        setTimeout(() => {
          playOrderSound();
          showBrowserNotif(order);

          // Show in-app banner (for first new order only to avoid spam)
          if (idx === 0) {
            setNotifBanner(order);
            // Auto-dismiss after 8 seconds
            setTimeout(() => setNotifBanner(null), 8000);
          }

          setNewOrderBadge(prev => prev + 1);

          // Toast as fallback
          toast.success(
            `🛒 New Order! ${order.order_number} — ₹${parseFloat(order.total_amount).toFixed(2)}`,
            { duration: 5000, id: `order-${order.id}` }
          );
        }, idx * 400);
      });

    } catch (_) {}
  }, [showBrowserNotif]);

  // Poll for low stock products
  const pollLowStock = useCallback(async () => {
    try {
      const res   = await getLowStockProducts();
      const items = res.data.data || [];
      setLowStockCount(items.length);

      // Detect newly low-stock products (not in previous set)
      const currentIds = new Set(items.map(p => p.id));
      const newlyLow   = items.filter(p => !prevLowStockIds.current.has(p.id));

      if (prevLowStockIds.current.size > 0 && newlyLow.length > 0) {
        // Show alert only for newly low items
        setLowStockBanner(newlyLow);
        setTimeout(() => setLowStockBanner(null), 10000);

        // Browser notification
        if (Notification.permission === 'granted') {
          try {
            new Notification(`⚠️ Low Stock Alert — ${newlyLow.length} product${newlyLow.length > 1 ? 's' : ''}`, {
              body: newlyLow.slice(0, 3).map(p => `${p.name}: ${p.stock_quantity} left`).join('\n'),
              icon: '/favicon.ico',
              tag: 'low-stock',
            });
          } catch (_) {}
        }
      }

      prevLowStockIds.current = currentIds;
    } catch (_) {}
  }, []);

  // Start polling when component mounts
  useEffect(() => {
    // Restore lastKnownId from localStorage on remount
    const stored = parseInt(localStorage.getItem('lastKnownOrderId') || '0');
    if (stored > 0) {
      lastOrderIdRef.current = stored;
      initializedRef.current = true;
    }

    requestNotifPermission();
    pollOrders();   // immediate first poll
    pollLowStock(); // immediate first low-stock check

    pollTimerRef.current = setInterval(() => {
      pollOrders();
      pollLowStock();
    }, POLL_INTERVAL);
    return () => clearInterval(pollTimerRef.current);
  }, [pollOrders, pollLowStock, requestNotifPermission]);

  // Clear badge when user visits Orders page
  useEffect(() => {
    const handleFocus = () => {
      if (window.location.pathname === '/orders') {
        setNewOrderBadge(0);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // ── Full screen toggle (whole admin panel) ────────────────────────────────
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const onFsChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);
  const toggleFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      (el.requestFullscreen || el.webkitRequestFullscreen || (() => {})).call(el);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen || (() => {})).call(document);
    }
  };

  const handleLogout = () => { localStorage.removeItem('adminToken'); navigate('/login'); };

  const visibleNav = ALL_NAV
    .filter(section => !isCashier || !section.adminOnly)
    .map(section => ({
      ...section,
      items: section.items.filter(item => !isCashier || !item.adminOnly),
    }))
    .filter(section => section.items.length > 0);

  return (
    <div className="layout">

      {/* ── New Order Banner ──────────────────────────────────────────────── */}
      {notifBanner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: 'linear-gradient(135deg, #0c831f, #34a853)',
          color: '#fff', padding: '12px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(12,131,31,0.5)',
          animation: 'slideDown 0.35s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'pulse 1s infinite',
            }}>
              <ShoppingCart size={18} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                🛒 New Online Order Received!
              </div>
              <div style={{ fontSize: 13, opacity: 0.92, marginTop: 1 }}>
                {notifBanner.order_number} &nbsp;·&nbsp; ₹{parseFloat(notifBanner.total_amount).toFixed(2)}
                {notifBanner.customer_name && ` · ${notifBanner.customer_name}`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => { navigate('/orders'); setNotifBanner(null); setNewOrderBadge(0); }}
              style={{
                background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff', borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
              }}>
              View Order
            </button>
            <button
              onClick={() => setNotifBanner(null)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', padding: 4 }}>
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* ── Low Stock Alert Banner ───────────────────────────────────────── */}
      {lowStockBanner && lowStockBanner.length > 0 && (
        <div style={{
          position: 'fixed', top: notifBanner ? 64 : 0, left: 0, right: 0, zIndex: 9998,
          background: 'linear-gradient(135deg, #ff9500, #ff6b00)',
          color: '#fff', padding: '10px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 4px 16px rgba(255,149,0,0.4)',
          animation: 'slideDown 0.35s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AlertTriangle size={18} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                ⚠️ Low Stock Alert — {lowStockBanner.length} product{lowStockBanner.length > 1 ? 's' : ''} running low!
              </div>
              <div style={{ fontSize: 12, opacity: 0.92, marginTop: 1 }}>
                {lowStockBanner.slice(0, 3).map(p => `${p.name} (${p.stock_quantity} left)`).join(' · ')}
                {lowStockBanner.length > 3 && ` · +${lowStockBanner.length - 3} more`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { navigate('/products'); setLowStockBanner(null); }}
              style={{ background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              View Products
            </button>
            <button onClick={() => setLowStockBanner(null)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', padding: 4 }}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <aside className="sidebar">
        {/* Logo */}
        <div className="sidebar-logo">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="logo-icon">
              <Store size={20} color="#0C831F" />
            </div>
            <div>
              <h2>MS Supermart</h2>
              <p>{isCashier ? 'Cashier' : 'Admin'} Dashboard</p>
            </div>
          </div>
        </div>

        {/* Notification enable button (if not granted) */}
        {!notifEnabled && (
          <div
            onClick={requestNotifPermission}
            style={{
              margin: '0 12px 8px', padding: '8px 12px', borderRadius: 8,
              background: 'rgba(255,149,0,0.12)', border: '1px solid rgba(255,149,0,0.3)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            }}>
            <Bell size={13} color="var(--warning)" />
            <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>
              Enable order alerts
            </span>
          </div>
        )}

        {/* Nav */}
        <nav className="sidebar-nav">
          {visibleNav.map(({ section, items }) => (
            <div key={section}>
              <div className="nav-section-label">{section}</div>
              {items.map(({ path, icon: Icon, label }) => (
                <NavLink
                  key={path}
                  to={path}
                  end={path === '/'}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => { if (path === '/orders') setNewOrderBadge(0); }}
                >
                  <Icon size={16} />
                  {label}
                  {/* Badge on Orders */}
                  {path === '/orders' && newOrderBadge > 0 && (
                    <span style={{
                      marginLeft: 'auto', background: '#ff3b30', color: '#fff',
                      borderRadius: 10, fontSize: 10, fontWeight: 700,
                      padding: '2px 6px', minWidth: 18, textAlign: 'center',
                      animation: 'pulse 1s infinite',
                    }}>
                      {newOrderBadge}
                    </span>
                  )}
                  {/* Badge on Products for low stock */}
                  {path === '/products' && lowStockCount > 0 && (
                    <span style={{
                      marginLeft: 'auto', background: '#ff9500', color: '#fff',
                      borderRadius: 10, fontSize: 10, fontWeight: 700,
                      padding: '2px 6px', minWidth: 18, textAlign: 'center',
                    }}>
                      {lowStockCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '0 6px 10px', fontWeight: 600 }}>
            {isCashier ? '👤 Cashier mode' : '🔑 Admin mode'}
          </div>
          <button
            onClick={toggleFullscreen}
            title={isFull ? 'Exit full screen' : 'Full screen'}
            style={{
              width: '100%', marginBottom: 8, padding: '8px 12px', borderRadius: 8,
              background: 'var(--bg, #f4f4f6)', border: '1px solid var(--border, #e3e3e8)',
              color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600, fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
            {isFull ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            {isFull ? 'Exit Full Screen' : 'Full Screen'}
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={15} /> Logout
          </button>
        </div>
      </aside>

      <main className="main-content" style={{ marginTop: (notifBanner ? 64 : 0) + (lowStockBanner ? 52 : 0), transition: 'margin-top 0.3s' }}>
        <Outlet />
      </main>

      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
